import React from 'react';
import {
  TextField,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { RegisterData } from '../../../../../types/auth';
import { useUsernameValidation } from '../../../../../hooks/useUsernameValidation';

const StudentStep = () => {
  const { control, formState: { errors }, watch } = useFormContext<RegisterData>();
  const username = watch('profile.username') || '';
  const { isChecking, isAvailable, message, isRateLimited, isError } = useUsernameValidation(username);

  // Determine the error state and helper text
  const getHelperText = () => {
    if (errors.profile?.username) {
      return errors.profile.username.message;
    }
    if (username && username.length >= 3) {
      if (isChecking) {
        return 'Checking availability...';
      }
      if (isRateLimited) {
        return message || 'Too many username checks. Please wait and try again.';
      }
      if (isError) {
        return message || 'Could not verify username availability. You may proceed.';
      }
      if (isAvailable === false) {
        return message || 'Username is already taken';
      }
      if (isAvailable === true) {
        return 'Username is available';
      }
    }
    return 'This will be displayed on leaderboards';
  };

  // Show error for validation failures and unavailable usernames, but not for rate limiting or other errors
  const hasError = Boolean(errors.profile?.username || (username && username.length >= 3 && isAvailable === false));

  return (
    <>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>
        Please complete your student profile
      </Typography>

      <Controller
        name="profile.username"
        control={control}
        rules={{
          required: 'Username is required',
          minLength: {
            value: 3,
            message: 'Username must be at least 3 characters'
          },
          maxLength: {
            value: 20,
            message: 'Username must be less than 20 characters'
          },
          pattern: {
            value: /^[a-zA-Z0-9_]+$/,
            message: 'Username can only contain letters, numbers, and underscores'
          },
          validate: (value) => {
            // Only block if username is explicitly unavailable (not rate limited or error)
            if (value && value.length >= 3 && isAvailable === false && !isRateLimited && !isError) {
              return 'Username is already taken';
            }
            return true;
          }
        }}
        render={({ field }) => (
          <TextField
            value={field.value || ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            inputRef={field.ref}
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            autoComplete="username"
            error={hasError}
            helperText={getHelperText()}
            inputProps={{ 'data-testid': 'student-username-input' }}
            InputProps={{
              endAdornment: isChecking ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : undefined
            }}
          />
        )}
      />

      <Controller
        name="profile.school"
        control={control}
        rules={{
          required: 'School name is required'
        }}
        render={({ field }) => (
          <TextField
            value={field.value || ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            inputRef={field.ref}
            margin="normal"
            required
            fullWidth
            id="school"
            label="School Name"
            error={!!errors.profile?.school}
            helperText={errors.profile?.school?.message}
            inputProps={{ 'data-testid': 'student-school-input' }}
          />
        )}
      />

      <Controller
        name="profile.grade"
        control={control}
        rules={{
          required: 'Grade level is required',
          min: {
            value: 5,
            message: 'Grade must be between 5 and 8'
          },
          max: {
            value: 8,
            message: 'Grade must be between 5 and 8'
          }
        }}
        render={({ field }) => (
          <TextField
            value={field.value || ''}
            onChange={(e) => {
              const val = e.target.value;
              field.onChange(val ? Number(val) : undefined);
            }}
            onBlur={field.onBlur}
            name={field.name}
            inputRef={field.ref}
            margin="normal"
            required
            fullWidth
            id="grade"
            label="Grade Level"
            type="number"
            inputProps={{ 'data-testid': 'student-grade-input', min: 5, max: 8 }}
            error={!!errors.profile?.grade}
            helperText={errors.profile?.grade?.message || 'Grades 5-8'}
          />
        )}
      />

      <Controller
        name="profile.parentEmail"
        control={control}
        rules={{
          required: 'Parent email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address'
          }
        }}
        render={({ field }) => (
          <TextField
            value={field.value || ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            inputRef={field.ref}
            margin="normal"
            required
            fullWidth
            id="parentEmail"
            label="Parent Email"
            autoComplete="email"
            error={!!errors.profile?.parentEmail}
            helperText={errors.profile?.parentEmail?.message}
            inputProps={{ 'data-testid': 'student-parentEmail-input' }}
          />
        )}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        All fields are required to complete your registration. This information helps us provide the best experience for our math competitions.
      </Typography>
    </>
  );
};

export default StudentStep;
